/**
 * This file generates SEALED fingerprints - encrypted device identifiers that:
 * - Can only be decrypted by the backend
 * - Use special "sealed" API keys (different from regular FingerprintJS Pro)
 * - Include sophisticated fallback to expired cache when script fails
 *
 * RELATIONSHIP TO device.ts:
 * - This generates the sealed result that backend uses to extract visitorId
 * - Backend then sets the df_pro cookie with that visitorId
 * - device.ts is LEGACY and not used in production anymore
 */

import { ActionType, ComponentType, logEvent, logStatement } from '@cbhq/client-analytics';
import { getErrorObject } from '@cbhq/identity-shared/utils';

import { fingerprintStore } from '../../store';
import { Fingerprint, InitConfig } from '../../types';
import { getAgent, SDKLoadError } from '../../utils/fingerprintProAgent';
import { PACKAGE_VERSION } from '../../version';

// Cache key for localStorage
const SEALED_CACHE_KEY = 'sealed_df_pro';

// Default cache TTL. Callers may override via expirationMs.
export const SEALED_CACHE_EXPIRATION_MS = 1000 * 60 * 60; // 1 hour

type SealedFingerprintCache = {
  sealedFingerprint: Fingerprint;
  createdAt: number;
};

type SealedFingerprintProCacheRequest = {
  ignoreExpiration?: boolean;
  /** TTL in ms used to determine if the cached entry is stale (compared against `createdAt`). Defaults to SEALED_CACHE_EXPIRATION_MS (1 hour). */
  expirationMs?: number;
  /** @deprecated No longer used. Kept for backwards compatibility. */
  extendLifetime?: boolean;
};

/**
 * @description Private function to set the sealed fingerprint cache.
 * Only stores `createdAt` — staleness is determined at read time by the caller's TTL.
 */
function setSealedFingerprintProCache(sealedFingerprint: Fingerprint): Fingerprint {
  localStorage.setItem(
    SEALED_CACHE_KEY,
    JSON.stringify({
      sealedFingerprint,
      createdAt: Date.now(),
    }),
  );
  return sealedFingerprint;
}

/**
 * @description Get the sealed fingerprint from the cache
 * @param ignoreExpiration - If true, return cache regardless of age
 * @param expirationMs - TTL in ms. Cache entries older than this are considered stale. Defaults to SEALED_CACHE_EXPIRATION_MS (1 hour).
 * @returns sealedFingerprint: Fingerprint | undefined
 */
export function getSealedFingerprintProCache({
  ignoreExpiration = false,
  expirationMs = SEALED_CACHE_EXPIRATION_MS,
}: SealedFingerprintProCacheRequest = {}): Fingerprint | undefined {
  const cachedSealedFingerprint = localStorage.getItem(SEALED_CACHE_KEY);
  let cachedSealedFingerprintCache: SealedFingerprintCache | undefined;
  if (cachedSealedFingerprint) {
    try {
      cachedSealedFingerprintCache = JSON.parse(cachedSealedFingerprint);
    } catch {
      return undefined;
    }
  }

  if (!cachedSealedFingerprintCache) {
    return undefined;
  }

  // Reject entries without a createdAt (legacy) or older than the caller's TTL
  if (!ignoreExpiration) {
    const { createdAt } = cachedSealedFingerprintCache;
    if (createdAt === undefined || Date.now() - createdAt >= expirationMs) {
      return undefined;
    }
  }

  return cachedSealedFingerprintCache.sealedFingerprint;
}

/**
 * @description Get metadata about the sealed fingerprint cache
 * @returns Cache metadata including age and expiration status
 */
export function getSealedFingerprintCacheMetadata(): {
  exists: boolean;
  ageMinutes: number;
  isExpired: boolean;
  createdAt?: number;
} {
  const cachedSealedFingerprint = localStorage.getItem(SEALED_CACHE_KEY);

  if (!cachedSealedFingerprint) {
    return {
      exists: false,
      ageMinutes: -1,
      isExpired: false,
    };
  }

  try {
    const cache: SealedFingerprintCache = JSON.parse(cachedSealedFingerprint);
    const now = Date.now();
    const { createdAt } = cache;

    if (createdAt === undefined) {
      return { exists: true, ageMinutes: -1, isExpired: true };
    }

    const ageMs = now - createdAt;
    const ageMinutes = Math.floor(ageMs / (1000 * 60));

    return {
      exists: true,
      ageMinutes,
      isExpired: ageMs >= SEALED_CACHE_EXPIRATION_MS,
      createdAt,
    };
  } catch {
    return {
      exists: false,
      ageMinutes: -1,
      isExpired: false,
    };
  }
}

/**
 * @description Get the sealed fingerprint pro
 * @returns sealedFingerprint: Fingerprint | undefined
 */
export async function getSealedFingerprintPro(
  initConfig?: InitConfig,
): Promise<Fingerprint | undefined> {
  const config = fingerprintStore.initConfig ?? initConfig;

  if (!config) {
    logStatement('Sealed fingerprint pro called without initialized store or config params', {
      level: 'error',
    });
    return undefined;
  }

  const { ignoreStoredData } = config;
  // Prefer expirationMs from the direct parameter so callers can override per-call;
  // fall back to the value stored during initialization if no override is provided.
  const expirationMs = initConfig?.expirationMs ?? fingerprintStore.initConfig?.expirationMs;
  const startTime = performance.now();

  try {
    if (!ignoreStoredData) {
      const cachedSealedFingerprint = getSealedFingerprintProCache({ expirationMs });
      if (cachedSealedFingerprint) {
        const totalDuration = performance.now() - startTime;
        const cacheAge = cachedSealedFingerprint.createdAt
          ? Date.now() - cachedSealedFingerprint.createdAt
          : undefined;

        // Log that we're returning a cached fingerprint without calling SDK
        logEvent('identity_fingerprint_pro_completed', {
          action: ActionType.api,
          componentType: ComponentType.api,
          sealed: true,
          from_cache: true,
          perf_total_ms: Math.round(totalDuration),
          cache_age_ms: cacheAge,
          request_id: cachedSealedFingerprint.requestId,
          version: PACKAGE_VERSION,
        });

        return cachedSealedFingerprint;
      }
    }

    // Log that we're initiating an SDK call to generate a new fingerprint
    logEvent('identity_fingerprint_pro_request_started', {
      action: ActionType.api,
      componentType: ComponentType.api,
      sealed: true,
      version: PACKAGE_VERSION,
      isWebView: typeof window !== 'undefined' && !!window.ReactNativeWebView,
    });

    const fpAgent = await getAgent();

    let requestId = '';
    let sealedResult = '';
    let getDuration = 0;

    // Try shared promise first if available
    if (typeof window !== 'undefined' && window.fpGenerationPromise) {
      try {
        const sharedStartTime = performance.now();
        const result = await window.fpGenerationPromise;

        // Clear the promise immediately after use so regenerations get fresh fingerprints
        window.fpGenerationPromise = undefined;

        if (result.requestId && result.sealedResult) {
          const fingerprint: Fingerprint = {
            requestId: result.requestId,
            sealedResult: result.sealedResult,
            createdAt: Date.now(),
          };
          setSealedFingerprintProCache(fingerprint);

          requestId = result.requestId;
          sealedResult = result.sealedResult;
          getDuration = performance.now() - sharedStartTime;
        }
      } catch {
        // Clear promise on error
        window.fpGenerationPromise = undefined;
        // Shared promise failed, will generate below
      }
    }

    // Generate new if we don't have a result yet
    if (!requestId || !sealedResult) {
      // Check for failure mock mode
      if (typeof window !== 'undefined' && window.fpFM) {
        throw new Error('Client timeout');
      }

      const getStartTime = performance.now();
      const result = await fpAgent.get();
      requestId = result.requestId;
      sealedResult = result.sealedResult;
      getDuration = performance.now() - getStartTime;
    }

    if (!requestId || !sealedResult) {
      throw new Error('Fingerprint pro fetch returned empty fingerprint');
    }

    const fingerprint = { requestId, sealedResult, createdAt: Date.now() };
    setSealedFingerprintProCache(fingerprint);

    const totalDuration = performance.now() - startTime;

    // Log successful fingerprint generation from SDK with performance metrics
    logEvent('identity_fingerprint_pro_completed', {
      action: ActionType.api,
      componentType: ComponentType.api,
      sealed: true,
      perf_sdk_load_ms: fingerprintStore.timing.sdkLoadTime
        ? Math.round(fingerprintStore.timing.sdkLoadTime)
        : undefined,
      perf_generation_ms: Math.round(getDuration),
      perf_total_ms: Math.round(totalDuration),
      from_cache: false,
      request_id: requestId,
      version: PACKAGE_VERSION,
      isWebView: typeof window !== 'undefined' && !!window.ReactNativeWebView,
    });

    return fingerprint;
  } catch (e) {
    const error = getErrorObject(e);
    const elapsedMs = Math.round(performance.now() - startTime);

    // Determine error type based on error class
    const isSDKError =
      e instanceof SDKLoadError || (e as unknown as { name?: string })?.name === 'SDKLoadError';
    const errorType = isSDKError ? 'sdk_load_error' : 'generation_error';

    logStatement('Sealed fingerprint pro fetch error', {
      level: 'error',
      metadata: {
        error: error?.message ?? 'Unknown error',
        errorType,
        loadMethod: (e as SDKLoadError)?.loadMethod,
      },
    });

    // Log fingerprint generation or SDK loading failure with error details
    logEvent('identity_fingerprint_pro_error', {
      action: ActionType.api,
      componentType: ComponentType.api,
      error_type: errorType,
      error: error?.message,
      load_method: (e as SDKLoadError)?.loadMethod,
      fallback_triggered: (e as SDKLoadError)?.fallbackTriggered,
      perf_total_ms: elapsedMs,
      sealed: true,
      version: PACKAGE_VERSION,
    });

    // In case of error, we want to use the cached value
    // This is to avoid problems in case of fingerprint pro is not working properly
    // The BE must activate the fallback mechanism in this case so old sealed fingerprint is acceptable
    const cachedSealedFingerprint = getSealedFingerprintProCache({ ignoreExpiration: true });
    return {
      requestId: cachedSealedFingerprint?.requestId ?? '',
      sealedResult: cachedSealedFingerprint?.sealedResult ?? '',
      failed: true,
      createdAt: cachedSealedFingerprint?.createdAt ?? Date.now(),
    };
  }
}
